import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReMultiSelect from 'react-select';
import ReMultiSelectAnimated from 'react-select/animated';
import '../ChatApp.css';
import Spinner from 'react-bootstrap/Spinner';
import axios from 'axios';
import {showToast as toast} from '../toastManager';
import errorMessages from '../errorMessages.js';
import {v4 as uuidv4} from 'uuid';
import scalex_ai_logo from "../images/scalex_ai_logo.png"

function getMatchTypeLetter(matchType) {
    switch(matchType?.toLowerCase()) {
        case 'exact': return 'e';
        case 'phrase': return 'p';
        case 'broad': return 'b';
        default: return matchType || '';
    }
}

function get_keyword_targeting(keyword_dict) {
    return keyword_dict?.["value"]?.[0]["campaign_id"] + "-" + keyword_dict?.["value"]?.[0]["ad_group_id"] + "-" + keyword_dict?.["value"]?.[0]["keyword_text"] + "-" + getMatchTypeLetter(keyword_dict?.["value"]?.[0]["match_type"]);
}

function AIModeLpCustomization({ViewedPage, shop_domain, app_url}) {
    ViewedPage(31);

    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    const target = useRef(null);

    const [loading, setLoading] = useState(true);
    const [submit_btn_loading, setSubmitBtnLoading] = useState(false);
    const [show_settings, setShowSettings] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');

    const [all_lp_urls, setAllLpUrls] = useState([]);
    const [raw_campaigns, setRawCampaigns] = useState({});
    const [campaign_options, setCampaignOptions] = useState([]);

    const [allCamapignTargetings, setAllCamapignTargetings] = useState([]);
    const [allCampaigns, setAllCampaigns] = useState([]);

    const [selected_lp_urls, setSelectedLpUrls] = useState({});
    const [selected_campaign, setSelectedCampaign] = useState({});
    const [selected_template, setSelectedTemplates] = useState({});
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
    setMounted(true);
    }, []);

    useEffect(() => {
        setLoading(true);
        try{            
            const fetchCampaigns = async () => {
                try {
                    const response = await axios.get(`${app_url}/api/store/personalized_campaigns`, {withCredentials: true});
                    if (response.data.success) {
                        const campaigns = Array.isArray(response.data.data) ? response.data.data : [];

                        setAllCampaigns(campaigns);

                        const lpCampaignMap = {};

                        campaigns.forEach(campaign => {
                            const lpUrl = campaign?.landing_page_urls;
                            if(lpUrl){
                                const cleanedUrl = lpUrl.trim();
                                if(!lpCampaignMap[cleanedUrl]){
                                    lpCampaignMap[cleanedUrl] = [];
                                }
                                lpCampaignMap[cleanedUrl].push(campaign);
                            }
                        });

                        const ai_lp_urls = Object.keys(lpCampaignMap).map(url => ({value: url, label: url}));

                        const targetingPerLandingPage = {};
                        Object.entries(lpCampaignMap).forEach(([url, lpCampaigns]) => {
                            const targetingSet = new Set();
                            lpCampaigns
                                .filter(c => c.targeting_and_content && Array.isArray(c.targeting_and_content))
                                .forEach(c => {
                                    const isKeyword = c.category === 'Keyword Personalization';
                                    c.targeting_and_content
                                        .filter(tc => tc && Array.isArray(tc.targeting))
                                        .flatMap(tc => [...tc.targeting, tc.preview_targeting])
                                        .filter(Boolean)
                                        .forEach(iter => {
                                            let key;
                                            if (isKeyword) {
                                                if (iter?.value) key = get_keyword_targeting(iter);
                                                else if (typeof iter === 'string') key = iter;
                                            } else {
                                                key = iter?.label ? iter.label.toLowerCase() : iter.toLowerCase();
                                            }
                                            if (key) targetingSet.add(key);
                                        });
                                });
                            targetingPerLandingPage[url] = [...targetingSet];
                        });
                        setAllCamapignTargetings(targetingPerLandingPage);

                        setAllLpUrls(ai_lp_urls);
                        setRawCampaigns(lpCampaignMap);
                        setLoading(false); 
                    } else {            
                        setLoading(false);
                        toast.error("Failed to fetch campaigns");
                    }
                } catch (error) {
                    setLoading(false);
                    console.error("Error fetching campaigns:", error);
                    toast.error(errorMessages["FETCH_TEMPLATES_ERROR"]?.title || "Failed to fetch campaigns");
                }
            };
    
            fetchCampaigns();

            let chatbot_ai_lp_customization_data = sessionStorage.getItem(`chatbot_ai_lp_customization_${shop_domain.replace(".myshopify.com", "")}`);
            if(chatbot_ai_lp_customization_data){
                setMessages(JSON.parse(chatbot_ai_lp_customization_data));
            }

        }
        catch(error){
            console.error("Error:",error);
            toast.error(`${errorMessages["DATA_LOAD_ERROR"]["title"]}`);
            setLoading(false);
        }
    },[app_url, shop_domain])

    useEffect(() => {
        if (messagesEndRef.current) {
          // Scroll to the bottom of the div
          messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
        }
    }, [messages]);
    
    const handle_settings_hide = () => setShowSettings(false);
    const handle_settings_show = () => setShowSettings(true);
    const handle_settings_submit = (e) => {
        handle_settings_hide();
    }

    // Handler for editing AI response
    const handleEditAIResponse = (aiResponseData) => {
        let template = aiResponseData?.template;
        let responseKey = aiResponseData?.key;
        let landingPageUrl = aiResponseData?.url;

        // Create campaign data structure similar to CustomCampaignPersonalization
        const campaignData = {
            campaign_id: uuidv4(),
            campaign_name: `AI Generated Campaign - ${responseKey || 'Untitled'}`,
            status: 'draft',
            category: 'AI Landing Page',
            landing_page_urls: landingPageUrl,
            templates: template,
            sections_rearrangement: aiResponseData?.sections_rearrangement || selected_template["sections_rearrangement"] || [],
            approved_sections: aiResponseData?.approved_sections || selected_template["approved_sections"] || [],
            targeting_and_content: []
        };

        // Create targeting item structure
        const targetingItem = {
            id: responseKey || uuidv4(),
            name: `AI Generated Variant - ${responseKey || 'Untitled'}`,
            status: 'paused',
            targeting: [responseKey],
            value: aiResponseData?.value || {},
            lpPreview: aiResponseData?.customized_lp_url || encodeURI(`${landingPageUrl}?sxi_content=${responseKey}`),
            preview_targeting: responseKey
        };

        campaignData["targeting_and_content"] = [targetingItem];

        // Navigate to AI Response Editor with the necessary data
        navigate('/ai-response-editor', {
            state: {
                campaignData: campaignData,
                targetingItem: targetingItem,
                allTargeting: {
                    ...allCamapignTargetings,
                    [landingPageUrl]: [...new Set([...(allCamapignTargetings[landingPageUrl] || []), responseKey.toLowerCase()])]
                },
                previousPage: 'ai-mode-lp-customization'
            }
        });
    };



    // Handles form submission when the user sends a message
    const handleSubmit = (e) => {
        e.preventDefault();

        if (input.trim()) {
            if(!selected_lp_urls || Object.entries(selected_lp_urls).length === 0){
                toast.error("Please select a landing page URL");
                return;
            }
            
            if(!selected_campaign || Object.entries(selected_campaign).length === 0){
                toast.error("Please select a campaign");
                return;
            }

            if(!selected_template || Object.entries(selected_template).length === 0){
                toast.error("Selected campaign does not have a template");
                return;
            }

            const newMessage = { role: 'user', content: input };
            setMessages([...messages, newMessage]);
            setInput(''); // clear the input field after sending
            
            setSubmitBtnLoading(true);
            try{
                let selected_template_context = selected_template["value"]?.context || "";
                let selected_template_question_prompt = selected_template["value"]?.question_prompt || "";
                let selected_template_customized_sections = selected_template["value"]?.customized_sections.filter(section => section?.options && section?.options?.length > 0) || [];
                selected_template_customized_sections = selected_template_customized_sections.map(section => section.options.map(option => option.value)).flat();
                let selected_template_rearrangement_sections = selected_template["sections_rearrangement"] || [];
                selected_template_rearrangement_sections = selected_template_rearrangement_sections.map(({ section_id, section_description, section_rearrangement }) => ({
                    section_id,
                    section_description,
                    section_rearrangement
                  }))

                let selected_template_approved_sections = selected_template["approved_sections"] || [];
                selected_template_approved_sections = selected_template_approved_sections.map(({ section_id, section_description, section_rearrangement }) => ({
                    section_id,
                    section_description,
                    section_rearrangement
                }))
    
                let new_message = `User Question : ${input}`;
                if (selected_template_context) {
                    new_message = `${new_message}\n\n${selected_template_context}`;
                }
                if (selected_template_question_prompt) {
                    new_message = `${new_message}\n\n${selected_template_question_prompt}`;
                }
                if(selected_template_customized_sections && selected_template_customized_sections.length > 0){
                    new_message = `Customization options: ${JSON.stringify(selected_template_customized_sections)}\n\n${new_message}`;
                }
                if(selected_template_rearrangement_sections && selected_template_rearrangement_sections.length > 0){
                    new_message = `All Sections: ${JSON.stringify([...selected_template_rearrangement_sections, ...selected_template_approved_sections])}\n\n${new_message}`;
                }

                console.log(new_message)
    
                axios.post(app_url + "/ai_lp_generation", {
                    new_message: new_message,
                    old_message: [],
                    format: "json_object",
                    send_business_context : false, 
                    template: selected_template["value"],
                    sections_rearrangement: selected_template["sections_rearrangement"],
                    approved_sections: selected_template["approved_sections"],
                    landing_page_url: selected_lp_urls["value"]
                }, {withCredentials: true})
                    .then(res => res.data)
                    .then(data => {
                        let chatbot_res = data.data;
                        console.log(chatbot_res);
                        let ai_res_message = [];
                        for(const [key_iter, value] of Object.entries(chatbot_res)){
                            let rearranged_lp_data = {
                                key: key_iter, 
                                value: value, 
                                url: selected_lp_urls["value"], 
                                customized_lp_url: encodeURI(`${selected_lp_urls["value"]}?sxi_content=${key_iter}`), 
                                template: selected_template["value"],
                                sections_rearrangement: selected_template["sections_rearrangement"] || [],
                                approved_sections: selected_template["approved_sections"] || []
                            };
                            ai_res_message.push({ role: 'assistant', content: `${encodeURI(`${selected_lp_urls["value"]}?sxi_content=${key_iter}`)}`, data: rearranged_lp_data });
                        }

                        setSubmitBtnLoading(false)
                        setMessages((prev) => [...prev, ...ai_res_message]);
                        sessionStorage.setItem(`chatbot_ai_lp_customization_${shop_domain.replace(".myshopify.com", "")}`, JSON.stringify([...messages, newMessage, ...ai_res_message]));
                    })
                    .catch(err => {
                        setSubmitBtnLoading(false)
                        toast.error(`${errorMessages["AI_GENERATION_ERROR"]["title"]}`);
                        console.log(err);
                    })
            }
            catch(error){
                setSubmitBtnLoading(false)
                toast.error(`${errorMessages["AI_GENERATION_ERROR"]["title"]}`);
                console.log(error);
            }
        }
    };

    return (
        <div className='figma-chat-layout'>
            {!shop_domain ? (
                <div className='go_to_shopify_admin_div'>
                    <h5 style={{ display: 'inline-block' }}>!! Go to Shopify Admin and open our app !!</h5>
                </div>
            ) : (
                <>
                    {!loading ? (
                        <div className={`ai-mode-layout ${show_settings ? 'sidebar-open' : ''}`}>
                            <div className='main-content-area'>
                                <div className='chat-header'>
                                    <div className='header-content'>
                                        <div className='header-title'>
                                            {/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="lightning-icon">
                                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                                            </svg> */}
                                            <h1>AI Mode</h1>
                                        </div>
                                        <p className='header-subtitle'>Generate personalized page variations with a simple prompt.</p>
                                    </div>
                                </div>

                                <div className='chat-messages-container' ref={messagesEndRef}>
                                    {messages.length === 0 ? (
                                        <div className='welcome-messages'>
                                            <div className='ai-message-figma'>
                                                <div className='ai-avatar-figma'>
                                                    <img width="32" height="31" src={scalex_ai_logo} alt="ScaleX AI Logo" />
                                                    {/* <svg width="32" height="31" viewBox="0 0 32 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M10.5102 7.14185L11.8727 9.2442M11.8727 9.2442L23.0206 16.0568M11.8727 9.2442L20.9785 7.14185L23.0206 16.0568M11.8727 9.2442V22.6217M11.8727 9.2442L5.974 15.4375L11.8727 22.6217M23.0206 16.0568L26.8604 15.4375M23.0206 16.0568L11.8727 22.6217M23.0206 16.0568L20.9785 24.2303L11.8727 22.6217M11.8727 22.6217L10.5102 24.7274" stroke="#2056C1"/>
                                                        <path d="M21.2116 6.47319L26.423 15.4996L21.2111 24.527L10.7881 24.5265L5.57665 15.5L10.7873 6.47237L21.2116 6.47319Z" stroke="#2056C1"/>
                                                    </svg> */}
                                                </div>
                                                <div className='message-content-figma'>
                                                    <div className='message-text'>Hello! I'm Cognitive UX, your prompt-to-page assistant to help you generate a personalized experience for every visitor</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        messages.map((msg, index) => (
                                            <div key={index} className={`message-figma ${msg.role === 'user' ? 'user-message-figma' : 'ai-message-figma'}`}>
                                                {msg.role === 'user' ? (
                                                    <>
                                                        <div className='user-avatar-figma'>
                                                            <svg width="42" height="43" viewBox="0 0 42 43" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <rect y="0.5" width="42" height="42" rx="21" fill="#E5EEFF"/>
                                                                <path d="M21 13.25C18.1087 13.25 15.75 15.6087 15.75 18.5C15.75 20.3075 16.6725 21.9125 18.0705 22.8597C15.3952 24.0072 13.5 26.6622 13.5 29.75H15C15 26.4275 17.6775 23.75 21 23.75C24.3225 23.75 27 26.4275 27 29.75H28.5C28.5 26.6622 26.6047 24.008 23.9295 22.859C24.6432 22.3775 25.2279 21.7284 25.6325 20.9684C26.037 20.2085 26.2491 19.3609 26.25 18.5C26.25 15.6087 23.8912 13.25 21 13.25ZM21 14.75C23.0798 14.75 24.75 16.4202 24.75 18.5C24.75 20.5798 23.0798 22.25 21 22.25C18.9202 22.25 17.25 20.5798 17.25 18.5C17.25 16.4202 18.9202 14.75 21 14.75Z" fill="#3840F7"/>
                                                            </svg>
                                                        </div>
                                                        <div className='message-content-figma'>
                                                            <div className='message-text'>{msg.content}</div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className='ai-avatar-figma'>
                                                            <img width={32} height={31} src={scalex_ai_logo} alt="ScaleX AI Logo" />
                                                            {/* <svg width="32" height="31" viewBox="0 0 32 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10.5102 7.14185L11.8727 9.2442M11.8727 9.2442L23.0206 16.0568M11.8727 9.2442L20.9785 7.14185L23.0206 16.0568M11.8727 9.2442V22.6217M11.8727 9.2442L5.974 15.4375L11.8727 22.6217M23.0206 16.0568L26.8604 15.4375M23.0206 16.0568L11.8727 22.6217M23.0206 16.0568L20.9785 24.2303L11.8727 22.6217M11.8727 22.6217L10.5102 24.7274" stroke="#2056C1"/>
                                                                <path d="M21.2116 6.47319L26.423 15.4996L21.2111 24.527L10.7881 24.5265L5.57665 15.5L10.7873 6.47237L21.2116 6.47319Z" stroke="#2056C1"/>
                                                            </svg> */}
                                                        </div>
                                                        <div className='message-content-figma'>
                                                            <div className='message-text'>
                                                                {msg?.data && msg?.data?.value && msg?.data?.value?.reasoning ? (
                                                                    <div className='reasoning-content'>
                                                                        {typeof (msg.data["value"]["reasoning"]) === "object" ? (
                                                                            <ul>{msg.data["value"]["reasoning"].map((reason, idx) => (
                                                                                <li key={idx}>{reason}</li>
                                                                            ))}</ul>
                                                                        ) : (
                                                                            <p>{msg.data["value"]["reasoning"]}</p>
                                                                        )}
                                                                    </div>
                                                                ) : null}
                                                                {msg?.data?.customized_lp_url && (
                                                                    <div className='link-content'>
                                                                        <a href={msg.data["customized_lp_url"]} target='_blank' rel="noopener noreferrer" className='preview-link-figma'>
                                                                            {msg.data["customized_lp_url"]}
                                                                        </a>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className='message-actions'>
                                                                <button
                                                                    className="edit-button-figma"
                                                                    onClick={() => handleEditAIResponse(msg.data)}
                                                                >
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                                    </svg>
                                                                    Edit
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className='chat-input-area'>
                                    <Form className='input-form-figma' onSubmit={handleSubmit}>
                                        <div className="input-field-wrapper">
                                            <input
                                                type="text"
                                                placeholder="Message Communication AI..."
                                                value={input}
                                                onChange={(e) => setInput(e.target.value)}
                                                className="chat-input-figma"
                                                disabled={submit_btn_loading}
                                            />
                                            <div className="input-buttons">
                                                {submit_btn_loading ? (
                                                    <button type="button" className="send-button-figma" disabled>
                                                        <Spinner animation="border" size="sm" />
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button type="submit" className="send-button-figma">
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="22" y1="2" x2="11" y2="13"/>
                                                                <polygon points="22,2 15,22 11,13 2,9 22,2"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="menu-button-figma"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handle_settings_show();
                                                            }}
                                                        >
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="3" y1="6" x2="21" y2="6"/>
                                                                <line x1="3" y1="12" x2="21" y2="12"/>
                                                                <line x1="3" y1="18" x2="21" y2="18"/>
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </Form>
                                </div>
                            </div>

                            {/* <div className={`right-sidebar ${show_settings ? 'show' : 'hide'}`}> */}
                            <div className={`right-sidebar ${mounted && show_settings ? 'show' : 'hide'}`}>
                                <div className='quick-actions-panel'>
                                    <div className='quick-actions-header'>
                                        <h3>Quick Actions</h3>
                                        <button className='close-button-figma' onClick={handle_settings_hide}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"/>
                                                <line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                        </button>
                                    </div>
                                    <div className='quick-actions-content'>
                                        <div className='action-section'>
                                            <div className='action-header'>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 8C1 9.85652 1.7375 11.637 3.05025 12.9497C4.36301 14.2625 6.14348 15 8 15C9.85652 15 11.637 14.2625 12.9497 12.9497C14.2625 11.637 15 9.85652 15 8C15 6.14348 14.2625 4.36301 12.9497 3.05025C11.637 1.7375 9.85652 1 8 1C6.14348 1 4.36301 1.7375 3.05025 3.05025C1.7375 4.36301 1 6.14348 1 8Z" stroke="white" strokeLinejoin="round"/>
                                                    <path d="M5 8C5 6.14348 5.31607 4.36301 5.87868 3.05025C6.44129 1.7375 7.20435 1 8 1C8.79565 1 9.55871 1.7375 10.1213 3.05025C10.6839 4.36301 11 6.14348 11 8C11 9.85652 10.6839 11.637 10.1213 12.9497C9.55871 14.2625 8.79565 15 8 15C7.20435 15 6.44129 14.2625 5.87868 12.9497C5.31607 11.637 5 9.85652 5 8Z" stroke="white" strokeLinejoin="round"/>
                                                    <path d="M1.5 10.3334H14.5M1.5 5.66675H14.5" stroke="white" strokeLinecap="round"/>
                                                </svg>
                                                <span className="label">Landing page URLs</span>
                                            </div>
                                            <ReMultiSelect
                                                id="lp_urls_value"
                                                closeMenuOnSelect={true}
                                                components={ReMultiSelectAnimated()}
                                                value={selected_lp_urls}
                                                options={all_lp_urls}
                                                onChange={(selected_vals) => {
                                                    setSelectedLpUrls(selected_vals);
                                                    setSelectedCampaign({});
                                                    setSelectedTemplates({});
                                                    const lpCampaigns = raw_campaigns[selected_vals?.value] || [];
                                                    const options = lpCampaigns.map(campaign => ({
                                                        label: campaign?.campaign_name || campaign?.campaign_id || 'Campaign',
                                                        value: campaign?.campaign_id,
                                                        campaign: campaign
                                                    }));
                                                    setCampaignOptions(options);
                                                }}
                                                className="figma-select"
                                                isClearable={false}
                                                isSearchable={false}
                                                noOptionsMessage={() => "No URLs available"}
                                            />
                                        </div>
                                        <div className='action-section'>
                                            <div className='action-header'>
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M11 1V3H1V1H11ZM11 0H1C0.734784 0 0.48043 0.105357 0.292893 0.292893C0.105357 0.48043 0 0.734784 0 1V3C0 3.26522 0.105357 3.51957 0.292893 3.70711C0.48043 3.89464 0.734784 4 1 4H11C11.2652 4 11.5196 3.89464 11.7071 3.70711C11.8946 3.51957 12 3.26522 12 3V1C12 0.734784 11.8946 0.48043 11.7071 0.292893C11.5196 0.105357 11.2652 0 11 0ZM3 6V11H1V6H3ZM3 5H1C0.734784 5 0.48043 5.10536 0.292893 5.29289C0.105357 5.48043 0 5.73478 0 6V11C0 11.2652 0.105357 11.5196 0.292893 11.7071C0.48043 11.8946 0.734784 12 1 12H3C3.26522 12 3.51957 11.8946 3.70711 11.7071C3.89464 11.5196 4 11.2652 4 11V6C4 5.73478 3.89464 5.48043 3.70711 5.29289C3.51957 5.10536 3.26522 5 3 5ZM11 6V11H6V6H11ZM11 5H6C5.73478 5 5.48043 5.10536 5.29289 5.29289C5.10536 5.48043 5 5.73478 5 6V11C5 11.2652 5.10536 11.5196 5.29289 11.7071C5.48043 11.8946 5.73478 12 6 12H11C11.2652 12 11.5196 11.8946 11.7071 11.7071C11.8946 11.5196 12 11.2652 12 11V6C12 5.73478 11.8946 5.48043 11.7071 5.29289C11.5196 5.10536 11.2652 5 11 5Z" fill="white"/>
                                                </svg>
                                                <span className="label">Campaigns</span>
                                            </div>
                                            <ReMultiSelect
                                                id="campaigns"
                                                closeMenuOnSelect={true}
                                                components={ReMultiSelectAnimated()}
                                                value={selected_campaign}
                                                options={campaign_options}
                                                onChange={(selected_vals) => {
                                                    setSelectedCampaign(selected_vals);
                                                    const campaignData = selected_vals?.campaign || {};
                                                    const campaignTemplate = campaignData?.templates;
                                                    const sections_rearrangement = campaignTemplate?.sections_rearrangement || campaignData?.sections_rearrangement || [];
                                                    const approved_sections = campaignTemplate?.approved_sections || campaignData?.approved_sections || [];
                                                    setSelectedTemplates({
                                                        label: campaignTemplate?.name || campaignData?.campaign_name || "Campaign Template",
                                                        value: campaignTemplate,
                                                        sections_rearrangement: sections_rearrangement,
                                                        approved_sections: approved_sections
                                                    });
                                                }}
                                                className="figma-select"
                                                isClearable={false}
                                                isSearchable={false}
                                                noOptionsMessage={() => "No campaigns available"}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='loading-container-figma' style={{'width':'1000px'}}>
                            <Spinner animation="border" />
                            <p>Loading AI Mode...</p>
                        </div>
                    )}
                </>
            )}
        </div>
    )

}

export default AIModeLpCustomization;



function exclude_all_previous_messages(messsages){
    let filtered_messages = [];
    for(let message of messsages){
        message["exclude"] = true;
        filtered_messages.push(message);
    }
    return filtered_messages;
}